import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import { WithDisposable } from '@blocksuite/global/lit';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { css, html, LitElement, nothing } from 'lit';
import { property } from 'lit/decorators.js';

export class EdgelessMinimap extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: block;
      margin-bottom: 8px;
    }

    .minimap-container {
      width: 220px;
      height: 140px;
      background: var(--affine-background-overlay-panel-color, rgba(255, 255, 255, 0.9));
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--affine-border-color, rgba(0, 0, 0, 0.1));
      border-radius: 10px;
      box-shadow: var(--affine-shadow-2, 0 4px 16px rgba(0, 0, 0, 0.15));
      overflow: hidden;
      position: relative;
      user-select: none;
      cursor: crosshair;
    }

    .minimap-svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    .minimap-header {
      position: absolute;
      top: 6px;
      left: 8px;
      font-size: 10px;
      font-weight: 600;
      color: var(--affine-text-secondary-color, rgba(120, 120, 120, 0.6));
      font-family: system-ui, -apple-system, sans-serif;
      pointer-events: none;
    }
  `;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get viewport() {
    return this.gfx.viewport;
  }

  private _getMinimapBounds() {
    const elementsBound = this.gfx.elementsBound;
    const viewportBounds = this.viewport.viewportBounds;

    // Base bounds on elements
    let xMin = elementsBound.x;
    let yMin = elementsBound.y;
    let w = elementsBound.w;
    let h = elementsBound.h;

    // Fallback if elements bound is empty/zero
    if (w <= 0 || h <= 0) {
      xMin = viewportBounds.x;
      yMin = viewportBounds.y;
      w = viewportBounds.w;
      h = viewportBounds.h;
    }

    // Add padding (15% of max dimension, minimum 150)
    const padding = Math.max(Math.max(w, h) * 0.15, 150);
    xMin -= padding;
    yMin -= padding;
    const worldW = w + padding * 2;
    const worldH = h + padding * 2;

    return { xMin, yMin, worldW, worldH };
  }

  private _handlePointerAction(e: PointerEvent) {
    const svg = this.shadowRoot?.querySelector('svg');
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const clickXPercent = (e.clientX - rect.left) / rect.width;
    const clickYPercent = (e.clientY - rect.top) / rect.height;

    const { xMin, yMin, worldW, worldH } = this._getMinimapBounds();

    const targetModelX = xMin + clickXPercent * worldW;
    const targetModelY = yMin + clickYPercent * worldH;

    this.viewport.setCenter(targetModelX, targetModelY);
  }

  private _onPointerDown(e: PointerEvent) {
    e.preventDefault();
    this._handlePointerAction(e);

    const onPointerMove = (moveEvent: PointerEvent) => {
      this._handlePointerAction(moveEvent);
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }

  private _onElementClick(el: any, e: PointerEvent) {
    e.stopPropagation();
    e.preventDefault();

    const b = el.elementBound;
    if (b) {
      this.viewport.setCenter(b.x + b.w / 2, b.y + b.h / 2);
    }

    this.gfx.selection.set({
      elements: [el.id],
      editing: false,
    });
  }

  override connectedCallback() {
    super.connectedCallback();

    const { disposables, std } = this;
    const slots = std.get(EdgelessLegacySlotIdentifier);

    // Re-render when viewport moves or zooms
    disposables.add(
      this.viewport.viewportUpdated.subscribe(() => this.requestUpdate())
    );

    // Re-render when block std store updates elements
    disposables.add(
      std.store.slots.blockUpdated.subscribe(() => this.requestUpdate())
    );

    disposables.add(
      slots.readonlyUpdated.subscribe(() => {
        this.requestUpdate();
      })
    );
  }

  override render() {
    if (this.std.store.readonly) {
      return nothing;
    }

    const { xMin, yMin, worldW, worldH } = this._getMinimapBounds();
    const viewportBounds = this.viewport.viewportBounds;
    const elements = this.gfx.gfxElements;

    return html`
      <div 
        class="minimap-container" 
        @pointerdown=${this._onPointerDown}
      >
        <div class="minimap-header">MINIMAP</div>
        <svg 
          class="minimap-svg" 
          viewBox="${xMin} ${yMin} ${worldW} ${worldH}"
        >
          <!-- Draw Canvas Objects -->
          ${elements.map(el => {
            const b = el.elementBound;
            if (!b || b.w <= 0 || b.h <= 0) return nothing;

            const flavour = el.flavour;
            const type = el.type;

            // Draw note / frames / standard shapes with varying fills
            let fill = 'rgba(128, 128, 128, 0.25)';
            let stroke = 'rgba(128, 128, 128, 0.45)';
            
            if (flavour === 'affine:note') {
              fill = 'rgba(234, 179, 8, 0.3)'; // amber/yellow
              stroke = 'rgba(234, 179, 8, 0.5)';
            } else if (flavour === 'affine:frame') {
              fill = 'rgba(168, 85, 247, 0.1)'; // purple frame outline
              stroke = 'rgba(168, 85, 247, 0.4)';
            } else if (type || flavour === 'affine:shape') {
              fill = 'rgba(59, 130, 246, 0.3)'; // blue shape
              stroke = 'rgba(59, 130, 246, 0.5)';
            }

            return html`
              <rect
                x=${b.x}
                y=${b.y}
                width=${b.w}
                height=${b.h}
                fill=${fill}
                stroke=${stroke}
                stroke-width=${Math.max(worldW / 120, 2)}
                rx=${Math.max(worldW / 150, 2)}
                style="cursor: pointer; pointer-events: auto;"
                @pointerdown=${(e: PointerEvent) => this._onElementClick(el, e)}
              />
            `;
          })}

          <!-- Draw Viewport Bounding Indicator -->
          <rect
            x=${viewportBounds.x}
            y=${viewportBounds.y}
            width=${viewportBounds.w}
            height=${viewportBounds.h}
            fill="rgba(59, 130, 246, 0.08)"
            stroke="var(--affine-primary-color, #3b82f6)"
            stroke-width=${Math.max(worldW / 80, 3)}
            rx=${Math.max(worldW / 120, 2)}
          />
        </svg>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor std!: BlockStdScope;
}
